import React from 'react';
import {
    SimpleShowLayout,
    TextField,
    BooleanField,
    SimpleForm,
    TextInput,
    ReferenceInput,
    NumberInput,
    SelectInput,
    required,
    email,
    maxLength,
    maxValue,
    minValue
} from 'admin-on-rest';
import {DependentInput} from 'aor-dependent-input';
import {sortNumber} from '../shared/tableFunctions';
import List from '../admin/List';
import Show from '../admin/Show';
import Create from '../admin/Create';
import Edit from '../admin/Edit';
import FilterWithSubmit from '../admin/FilterWithSubmit';
import ListTableContainer from '../components/ListTableContainer';
import LocationsWithIdsSelectInput from "../components/LocationsWithIdsSelectInput";
import POSUserTypeNameTextField from '../components/POSUserTypeNameTextField';
import POSUserDistrictLocationTextField from '../components/POSUserDistrictLocationTextField';
import POSUserTypePermissionsList from '../components/POSUserTypePermissionsList';
import ValidatedUserTextInput from "../components/ValidatedUserTextInput";
import VirtualizedSelectInput from '../components/VirtualizedSelectInput';
import ViewButton from '../buttons/ViewButton';
import EditButton from '../buttons/EditButton';
import TooltipIcon from '../components/TooltipIcon';
import ReactTooltip from 'react-tooltip';
import {validateRequiredSelect} from '../shared/formFunctions';
import {viewStyles} from '../shared/styles-view';
import {POS_USER_TYPES} from './posUserLists';
import {Card, CardHeader, CardText} from 'material-ui/Card';
import Avatar from 'material-ui/Avatar';
import Icon from 'material-ui/svg-icons/social/person';

export const fusePosUserIcon = Icon;

const styles = {
    fieldSmall: {flex: '0 1 10%', display: 'inline-block', maxWidth: '8.5em', overflow: 'hidden'},
};

/*
 * View: List
 */

const POSUserFilter = (props) => (
    <FilterWithSubmit
        {...props}
        updateOnChange={true}
        displayButton={false}
    >
        <SelectInput
            source="filter_active"
            label="Select Active Status:"
            optionValue="id"
            dependsOn="filter_by"
            dependsOnFilterValue={true}
            choices={[
                {id: 'all', name: 'All'},
                {id: 'active', name: 'Active'},
                {id: 'inactive', name: 'Inactive'}
            ]}
            options={{
                defaultValue: 'all'
            }}
            defaultValue="all"
            style={viewStyles.filterInline}
            alwaysOn
        />
        <SelectInput
            source="filter_level"
            label="Select User Level:"
            optionValue="level"
            dependsOn="filter_by"
            choices={POS_USER_TYPES}
            style={viewStyles.filterInline}
            defaultValue={null}
        />
        <ReferenceInput
            label="Select Location"
            source="filter_location"
            reference="LocationsForInventory"
            dependsOn="filter_by"
            perPage={10000}
            allowEmpty
            style={{...viewStyles.filterInline, ...viewStyles.filterHalf}}>
            <VirtualizedSelectInput
                addField={true} optionText="filter_name" optionValue="id"
                options={{
                    className: "full-width",
                    clearable: false,
                    placeholder: "Select Location"
                }}
            />
        </ReferenceInput>
        <SelectInput
            source="filter_by"
            label="Select by:"
            alwaysOn
            choices={[
                {id: 'filter_location', name: 'Location ID'},
                {id: 'filter_level', name: 'POS User Type'},

            ]}
            style={viewStyles.filterInline}
            defaultValue={null}
        />
    </FilterWithSubmit>
);

// Set title for List POS Users
const ListTitle = <span><Avatar icon={<Icon/>} style={viewStyles.avatar} className="avatar-title"/>POS Users</span>;

const ListDescription = () => {
    return (
        <span>
            <p>POS Users are users with permissions for logging into the POS System.</p>
            <p>Sort by the columns. Click a POS User's View icon to view its details, or Edit to modify.
                Click <strong>Create</strong> to add a new POS User.</p>
        </span>
    );
};

const ListDescriptionViewOnly = () => {
    return (
        <span>
            <p>POS Users are users with permissions for logging into the POS System.</p>
            <p>Sort by the columns. Click a POS User's View icon to view its details.</p>
        </span>
    );
};

const ListColumns = [
    {source: 'id', label: 'ID', type: 'text', sortMethod: (a, b) => sortNumber(a, b, 'ASC')},
    {
        source: 'district_id', label: 'District ID', type: 'text',
        hideOnFilter: 'filter_by', hideOnValue: ['filter_location'],
        hideOnSecondaryFilter: 'filter_level', hideOnSecondaryValue: ['4', '2', '1', '0'],
        sortMethod: (a, b) => sortNumber(a, b, 'ASC')
    },
    {
        source: 'district_name', label: 'District Name', type: 'text',
        hideOnFilter: 'filter_by', hideOnValue: ['filter_location'],
        hideOnSecondaryFilter: 'filter_level', hideOnSecondaryValue: ['4', '2', '1', '0']
    },
    {
        source: 'location_id', label: 'Location', type: 'text',
        hideOnFilter: 'filter_by', hideOnValue: ['filter_location'],
        width: 120, hideOnSecondaryFilter: 'filter_level', hideOnSecondaryValue: ['4', '3'],
        sortMethod: (a, b) => sortNumber(a, b, 'ASC')
    },
    {source: 'first_name', label: () => (<span>First<br/>Name</span>), type: 'text'},
    {source: 'last_name', label: () => (<span>Last<br/>Name</span>), type: 'text'},
    {
        source: 'active',
        label: 'Active',
        component: <BooleanField source="active" label="Active"/>,
        filterable: false,
        width: 90
    },
    {source: '', label: '', component: <ViewButton/>, filterable: false, sortable: false, width: 80},
    {source: '', label: '', component: <EditButton/>, filterable: false, sortable: false, width: 80}
];

const ListColumnsViewOnly = [
    {source: 'id', label: 'ID', type: 'text', sortMethod: (a, b) => sortNumber(a, b, 'ASC')},
    {
        source: 'district_id', label: 'District ID', type: 'text',
        hideOnFilter: 'filter_by', hideOnValue: ['filter_location'],
        hideOnSecondaryFilter: 'filter_level', hideOnSecondaryValue: ['4', '2', '1', '0'],
        sortMethod: (a, b) => sortNumber(a, b, 'ASC')
    },
    {
        source: 'district_name', label: 'District Name', type: 'text',
        hideOnFilter: 'filter_by', hideOnValue: ['filter_location'],
        hideOnSecondaryFilter: 'filter_level', hideOnSecondaryValue: ['4', '2', '1', '0']
    },
    {
        source: 'location_id', label: 'Location', type: 'text',
        hideOnFilter: 'filter_by', hideOnValue: ['filter_location'],
        width: 120, hideOnSecondaryFilter: 'filter_level', hideOnSecondaryValue: ['4', '3'],
        sortMethod: (a, b) => sortNumber(a, b, 'ASC')
    },
    {source: 'first_name', label: () => (<span>First<br/>Name</span>), type: 'text'},
    {source: 'last_name', label: () => (<span>Last<br/>Name</span>), type: 'text'},
    {
        source: 'active',
        label: 'Active',
        component: <BooleanField source="active" label="Active"/>,
        filterable: false,
        width: 90
    },
    {source: '', label: '', component: <ViewButton/>, filterable: false, sortable: false, width: 80}
];

export const fusePosUserList = (props) => (
    <List
        title={ListTitle}
        listType="table"
        desc={<ListDescription/>}
        filters={<POSUserFilter/>}
        requiresFilter={true}
        allowQueryString={false}
        pagination={null}
        {...props}
    >
        <ListTableContainer
            {...props}
            columns={ListColumns}
            perPage={20}
            currentSort={{field: 'id', order: 'ASC'}}
        />
    </List>
);

export const fusePosUserListViewOnly = (props) => (
    <List
        title={ListTitle}
        listType="table"
        desc={<ListDescriptionViewOnly/>}
        filters={<POSUserFilter/>}
        requiresFilter={true}
        allowQueryString={false}
        pagination={null}
        {...props}
    >
        <ListTableContainer
            {...props}
            columns={ListColumnsViewOnly}
            perPage={20}
            currentSort={{field: 'id', order: 'ASC'}}
        />
    </List>
);

/*
 * View: Show
 */

// Set title for Show POS User
const ShowTitle = ({record}) => {
    return <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                         className="avatar-title"/>{record ? `${record.id} - ${record.first_name} ${record.last_name}` : 'POS User'}</span>;
};

const ShowDescription = () => {
    return (
        <span>
            <p>Review POS User details below. To edit this POS User, click <strong>Edit</strong> above.</p>
        </span>
    );
};

/*
 * Show POS User Table
 */
export const fusePosUserShow = (props) => (
    <Show {...props} title={<ShowTitle/>} desc={<ShowDescription/>}>
        <SimpleShowLayout>
            <TextField source="id" label="ID" style={viewStyles.rowShowField}/>
            <TextField source="pin" label="PIN" style={viewStyles.rowShowField}/>
            <hr style={viewStyles.break}/>
            <POSUserTypeNameTextField source="level" label="User Type"/>
            <TextField source="first_name" label="First Name"/>
            <TextField source="last_name" label="Last Name"/>
            <POSUserDistrictLocationTextField styles={viewStyles.rowShowField}/>
            <hr style={viewStyles.break}/>
            <TextField source="email" label="Email"/>
            <BooleanField source="active" label="User is Active" elStyle={styles.fieldSmall}/>
        </SimpleShowLayout>
    </Show>
);

/*
 * View: Create form
 */

// Set title for Create POS User
const CreateTitle = <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                                  className="avatar-title"/>Create a POS User</span>;

const CreateDescription = () => {
    return (
        <span>
            <p>POS Users are used for logging into the POS System.</p>
            <p>You will need to populate different fields based on the POS User Type. District Managers require a
                District, while Store Managers and Clerks require a Location. Before creating a POS User of
                these Types, you must create at least
                one <a href="#/Districts/create" style={viewStyles.fieldLink}>District</a> and
                one <a href="#/Locations/create" style={viewStyles.fieldLink}>Location</a>.
            </p>
            <Card>
                <CardHeader
                    subtitle="Expand to review POS User Type Permissions."
                    actAsExpander={true}
                    showExpandableButton={true}
                />
                <CardText expandable={true}>
                    <POSUserTypePermissionsList/>
                </CardText>
            </Card>
        </span>
    );
};

// Assemble a label for User Level
const UserLevelSelectLabel = (choice) => `${choice.name} (Level ${choice.level})`;

// Set display for fields based on User Type
const displayExpiresEos = (typeValue) => typeValue === 0;
const displayDistrictSelect = (typeValue) => typeValue === 3;
const displayLocationSelect = (typeValue) => typeValue <= 2;

// Set defaults for Create form
const defaultCreateValues = {active: false, expires_eos: false};

/*
 * Create POS User form
 */
export const fusePosUserCreate = (props) => (
    <Create {...props} title={CreateTitle} desc={<CreateDescription/>}>
        <SimpleForm defaultValue={defaultCreateValues} redirect="list">
            <ValidatedUserTextInput
                source="id"
                fieldType="number"
                label="User ID*"
                buttonLabel="Check Availability"
                successMessage="This User ID is available."
                errorDuplicateMessage="A User with this ID already exists."
                errorValidateMessage="Need to validate User ID."
                errorRequiredMessage="A unique User ID number is required."
                errorLengthMessage="A User ID must be 10 characters or fewer."
                fieldResource="POSUsers"
                maxLength={10}
                isRequired={true}
            />
            <span style={viewStyles.fieldDescription}>
                Enter a unique numeric value for identifying this POS User (ex: 1234).
                <TooltipIcon dataFor="ttId"/>
                <ReactTooltip id="ttId" type="light" effect="solid" multiline={true} border={true} className="tooltip">
                    A <strong>User ID</strong> must be unique. It is used by the POS User when logging into the POS
                    System. It must be 10 characters or fewer.
                </ReactTooltip>
            </span>

            <NumberInput source="pin" label="PIN" step={1}
                         validate={[required, minValue(0, 'Must be a positive number.'), maxValue(9999999999, 'A PIN must be 10 characters or fewer.'), maxLength(10, 'A PIN must be 10 characters or fewer.')]}/>
            <span style={viewStyles.fieldDescription}>
                PIN used for validating POS User when logging into a Location from the POS System.
                <TooltipIcon dataFor="ttPIN"/>
                <ReactTooltip id="ttPIN" type="light" effect="solid" multiline={true} border={true} className="tooltip">
                    A <strong>PIN</strong> validates a POS User when logging into the POS
                    System. It must be 10 characters or fewer.
                </ReactTooltip>
            </span>

            <ReferenceInput
                label="User Type*"
                source="level"
                reference="POSUserTypes"
                sort={{field: 'level', order: 'DESC'}}
                allowEmpty
                validate={[required]}
            >
                <SelectInput optionValue="level" optionText={UserLevelSelectLabel}
                             style={styles.fieldWide} validate={[required]}/>
            </ReferenceInput>
            <span style={viewStyles.fieldDescription}>
                Select the POS User Type to determine what permissions this POS User will have.
                <br/>
                Additional fields may be required based on selection.
                <TooltipIcon dataFor="ttType"/>
                <ReactTooltip id="ttType" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    A <strong>POS User Type</strong> defines a permissions level for Users of the POS System. If
                    needed, review the list of permissions available to each Type above.
                </ReactTooltip>
            </span>

            <DependentInput dependsOn="level" resolve={displayDistrictSelect}>
                <ReferenceInput
                    label="Select a District*"
                    source="district_id"
                    reference="Districts"
                    validate={[required, validateRequiredSelect]}
                    perPage={10000}
                    sort={{field: 'name', order: 'ASC'}}
                    allowEmpty>
                    <VirtualizedSelectInput
                        addField={true}
                        optionText="name"
                        optionValue="id"
                        required={true}
                        options={{
                            clearable: false,
                            labelKey: "name",
                            placeholder: "Select a District*",
                            required: true,
                        }}
                    />
                </ReferenceInput>
                <span style={viewStyles.fieldDescription}>
                    District Managers require a <strong>District</strong>. (If District is not available, please
                    exit and go to <a href="#/Districts/create" style={viewStyles.fieldLink}>Add District</a>).
                    <TooltipIcon dataFor="ttDistrict"/>
                    <ReactTooltip id="ttDistrict" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        A <strong>District</strong> groups Physical Locations to a geographical location or to a
                        District Manager. Districts are used for Sale and Inventory Reporting to keep track of
                        Districts' progress.
                    </ReactTooltip>
                </span>
            </DependentInput>

            <DependentInput dependsOn="level" resolve={displayLocationSelect}>
                <LocationsWithIdsSelectInput
                    {...props}
                    source="location_id"
                    optionValue="id"
                    label="Select a Location*"
                    isRequired={true}
                    allowEmpty
                />
                <span style={viewStyles.fieldDescription}>
                    Store Managers and Clerks require a <strong>Location</strong>. (If Location is not available,
                    please exit and go to <a href="#/Locations/create" style={viewStyles.fieldLink}>Add Location</a>).
                </span>
            </DependentInput>

            <TextInput source="first_name" label="First Name" validate={[required]} style={viewStyles.rowField}/>

            <TextInput source="last_name" label="Last Name" validate={[required]} style={viewStyles.rowField}/>

            <TextInput source="email" label="Email" type="email" validate={[email]}/>

            <DependentInput dependsOn="level" resolve={displayExpiresEos}>
                <SelectInput source="expires_eos" label="User expires at End of Season?"
                             choices={[
                                 {id: true, name: 'Yes'},
                                 {id: false, name: 'No'},
                             ]}/>
                <span style={viewStyles.fieldDescription}>
                    Does this POS User expire at End of Season?
                    <TooltipIcon dataFor="ttExpire"/>
                    <ReactTooltip id="ttExpire" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        For some stores, a POS User may be a temporary employee hired for a season. Setting this
                        to <em>Yes</em> will mark this POS User to become inactive upon end of season.
                    </ReactTooltip>
                </span>
            </DependentInput>

            <SelectInput source="active" label="User is Active"
                         validate={[required]}
                         choices={[
                             {id: true, name: 'Active'},
                             {id: false, name: 'Inactive'},
                         ]}/>
            <span style={viewStyles.fieldDescription}>
                Select Active or Inactive.
                <TooltipIcon dataFor="ttActive"/>
                <ReactTooltip id="ttActive" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    <em>Active</em> POS Users are able to use ID and PIN to login to their Location on the POS System.
                    <br/>
                    <em>Inactive</em> POS Users are not able to login to a Location on the POS System.
                </ReactTooltip>
            </span>

            <span style={viewStyles.fieldInstructions}>
                After clicking <strong>Save</strong>, you will be redirected to the POS Users List.
            </span>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
        </SimpleForm>
    </Create>
);

/*
 * View: Edit form
 */

// Set title for Edit POS User
const EditTitle = ({record}) => {
    return <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                         className="avatar-title"/>Edit {record ? `${record.first_name} ${record.last_name}` : 'POS User'}</span>;
};

const EditDescription = () => {
    return (
        <span>
            <p>Modify fields in the tabs below. Click <strong>Save</strong> to store this POS User's details.</p>
            <p>If you change the POS User Type, you may need to populate different fields. A District Manager will
                have a District, while Store Managers and below have Locations.</p>
            <p>Districts and Locations must exist before assigning POS Users. Before reassigning a POS User's
                User Type, you must create at least
                one <a href="#/Districts/create" style={viewStyles.fieldLink}>District</a> and
                one <a href="#/Locations/create" style={viewStyles.fieldLink}>Location</a>.
            </p>
            <Card>
                <CardHeader
                    subtitle="Expand to review POS User Type Permissions."
                    actAsExpander={true}
                    showExpandableButton={true}
                />
                <CardText expandable={true}>
                    <POSUserTypePermissionsList/>
                </CardText>
            </Card>
        </span>
    );
};

/*
 * Edit POS User form
 */
export const fusePosUserEdit = (props) => (
    <Edit {...props} title={<EditTitle/>} desc={<EditDescription/>}>
        <SimpleForm>
            <TextField source="id" label="User ID"/>

            <NumberInput source="pin" label="PIN" step={1}
                         validate={[required, minValue(0, 'Must be a positive number.'), maxValue(9999999999, 'A PIN must be 10 characters or fewer.'), maxLength(10, 'A PIN must be 10 characters or fewer.')]}/>
            <span style={viewStyles.fieldDescription}>
                PIN used for validating POS User when logging into a Location from the POS System.
            </span>

            <ReferenceInput
                label="User Type*"
                source="level"
                reference="POSUserTypes"
                sort={{field: 'level', order: 'ASC'}}
                allowEmpty
                validate={[required]}>
                <SelectInput optionValue="level" optionText={UserLevelSelectLabel}
                             style={styles.fieldWide} validate={[required]}/>
            </ReferenceInput>
            <span style={viewStyles.fieldDescription}>
                The POS User Type determines what permissions this POS User has. If changed, additional fields may be
                required.
                <TooltipIcon dataFor="ttType"/>
                <ReactTooltip id="ttType" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    A <strong>POS User Type</strong> defines a permissions level for Users of the POS System. If
                    needed, review the list of permissions available to each Type above.
                </ReactTooltip>
            </span>

            <DependentInput dependsOn="level" resolve={displayDistrictSelect}>
                <ReferenceInput
                    label="Select a District*"
                    source="district_id"
                    reference="Districts"
                    validate={[required, validateRequiredSelect]}
                    perPage={10000}
                    sort={{field: 'name', order: 'ASC'}}
                    allowEmpty>
                    <VirtualizedSelectInput
                        addField={true}
                        optionText="name"
                        optionValue="id"
                        required={true}
                        options={{
                            clearable: false,
                            labelKey: "name",
                            placeholder: "Select a District*",
                            required: true,
                        }}
                    />
                </ReferenceInput>
                <span style={viewStyles.fieldDescription}>
                    District Managers require a <strong>District</strong>. (If District is not available, please
                    exit and go to <a href="#/Districts/create" style={viewStyles.fieldLink}>Add District</a>).
                    <TooltipIcon dataFor="ttDistrict"/>
                    <ReactTooltip id="ttDistrict" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        A <strong>District</strong> groups Physical Locations to a geographical location or to a
                        District Manager. Districts are used for Sale and Inventory Reporting to keep track of
                        Districts' progress.
                    </ReactTooltip>
                </span>
            </DependentInput>

            <DependentInput dependsOn="level" resolve={displayLocationSelect}>
                <LocationsWithIdsSelectInput
                    {...props}
                    source="location_id"
                    optionValue="id"
                    label="Select a Location*"
                    isRequired={true}
                    allowEmpty
                />
                <span style={viewStyles.fieldDescription}>
                    Store Managers and Clerks require a <strong>Location</strong>. (If Location is not available,
                    please exit and go to <a href="#/Locations/create" style={viewStyles.fieldLink}>Add Location</a>).
                </span>
            </DependentInput>

            <TextInput source="first_name" label="First Name" validate={[required]} style={viewStyles.rowField}/>
            <TextInput source="last_name" label="Last Name" validate={[required]} style={viewStyles.rowField}/>
            <TextInput source="email" label="Email" type="email" validate={[email]}/>

            <DependentInput dependsOn="level" resolve={displayExpiresEos}>
                <SelectInput source="expires_eos" label="User expires at End of Season?"
                             choices={[
                                 {id: true, name: 'Yes'},
                                 {id: false, name: 'No'},
                             ]}/>
                <span style={viewStyles.fieldDescription}>
                    Does this POS User expire at End of Season?
                    <TooltipIcon dataFor="ttExpire"/>
                    <ReactTooltip id="ttExpire" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        For some stores, a POS User may be a temporary employee hired for a season. Setting this
                        to <em>Yes</em> will mark this POS User to become inactive upon end of season.
                    </ReactTooltip>
                </span>
            </DependentInput>

            <SelectInput source="active" label="User is Active"
                         validate={[required]}
                         choices={[
                             {id: true, name: 'Active'},
                             {id: false, name: 'Inactive'},
                         ]}/>
            <span style={viewStyles.fieldDescription}>
                Select Active or Inactive.
                <TooltipIcon dataFor="ttActive"/>
                <ReactTooltip id="ttActive" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    <em>Active</em> POS Users are able to use ID and PIN to login to their Location on the POS System.
                    <br/>
                    <em>Inactive</em> POS Users are not able to login to a Location on the POS System.
                </ReactTooltip>
            </span>

            <span style={viewStyles.fieldInstructions}>
                After clicking <strong>Save</strong>, you will be redirected to the POS Users List.
            </span>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
        </SimpleForm>
    </Edit>
);



// WEBPACK FOOTER //
// ./src/Fuse/User/posUser.js